<template>
  <div class="min-h-screen bg-gray-50 p-6 dark:bg-gray-900">
    <div class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">教师认证管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">按教师管理当前认证状态，并保留完整申请记录。</p>
      </div>
      <button class="page-btn px-4 py-3" type="button" :disabled="loading" @click="reloadActiveTab">
        刷新数据
      </button>
    </div>

    <div class="mb-6 flex gap-2 rounded-2xl bg-white p-1 shadow-sm dark:bg-gray-800">
      <button
        type="button"
        class="tab-btn"
        :class="activeTab === 'applications' ? 'tab-btn-active' : ''"
        @click="switchTab('applications')"
      >
        申请记录
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="activeTab === 'teachers' ? 'tab-btn-active' : ''"
        @click="switchTab('teachers')"
      >
        教师视图
      </button>
    </div>

    <section class="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div v-if="activeTab === 'teachers'" class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">认证状态</span>
          <select v-model="teacherFilters.certificationStatus" class="form-input" @change="handleTeacherFilterChange">
            <option value="">全部认证状态</option>
            <option v-for="option in certificationStatusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">开课状态</span>
          <select v-model="teacherFilters.teachingStatus" class="form-input" @change="handleTeacherFilterChange">
            <option value="">全部开课状态</option>
            <option v-for="option in teachingStatusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">租户</span>
          <select v-model.number="teacherFilters.tenantId" class="form-input" @change="handleTeacherFilterChange">
            <option :value="0">全部租户</option>
            <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
              {{ tenant.name }}
            </option>
          </select>
        </label>
      </div>
      <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">状态</span>
          <select v-model="applicationFilters.status" class="form-input" @change="handleApplicationFilterChange">
            <option value="">全部状态</option>
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">租户</span>
          <select v-model.number="applicationFilters.tenantId" class="form-input" @change="handleApplicationFilterChange">
            <option :value="0">全部租户</option>
            <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
              {{ tenant.name }}
            </option>
          </select>
        </label>
      </div>
    </section>

    <section v-if="activeTab === 'teachers'" class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div v-if="loading" class="flex min-h-[280px] items-center justify-center">
        <div class="text-center">
          <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载教师认证状态...</p>
        </div>
      </div>

      <div v-else-if="teachers.length === 0" class="px-6 py-14 text-center">
        <p class="text-base font-medium text-gray-700 dark:text-gray-200">暂无教师资料</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">教师完成基础资料或提交认证后，会出现在这里。</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="table-th">教师</th>
              <th class="table-th">租户</th>
              <th class="table-th">认证状态</th>
              <th class="table-th">开课状态</th>
              <th class="table-th">最新申请</th>
              <th class="table-th">通过时间</th>
              <th class="table-th text-right">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr
              v-for="item in teachers"
              :key="item.profile.id || `${item.profile.tenantId}-${item.profile.tutorId}`"
              class="hover:bg-gray-50 dark:hover:bg-gray-900/40"
              :class="item.pendingApplication ? 'bg-blue-50/60 dark:bg-blue-950/20' : ''"
            >
              <td class="table-td">
                <div class="flex items-center gap-3">
                  <img
                    v-if="item.profile.avatarUrl"
                    :src="item.profile.avatarUrl"
                    alt="教师头像"
                    class="h-10 w-10 rounded-full object-cover"
                  />
                  <div v-else class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                    {{ teacherInitial(item) }}
                  </div>
                  <div>
                    <p class="font-medium text-gray-900 dark:text-gray-100">{{ teacherDisplayName(item) }}</p>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">用户 ID {{ item.profile.tutorId }}</p>
                  </div>
                </div>
              </td>
              <td class="table-td font-medium text-gray-900 dark:text-gray-100">{{ item.tenantName || item.profile.tenantId }}</td>
              <td class="table-td">
                <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="certificationStatusClass(item.profile.certificationStatus)">
                  {{ certificationStatusLabel(item.profile.certificationStatus) }}
                </span>
              </td>
              <td class="table-td">
                <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="teachingStatusClass(item.profile.teachingStatus)">
                  {{ teachingStatusLabel(item.profile.teachingStatus) }}
                </span>
              </td>
              <td class="table-td">
                <div v-if="item.pendingApplication" class="space-y-1">
                  <span class="inline-flex rounded-full bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white dark:bg-blue-500">
                    新申请待审核
                  </span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ applicationTypeLabel(item.pendingApplication.applicationType) }} · {{ displayValue(formatDateTime(item.pendingApplication.submittedAt || item.pendingApplication.updatedAt)) }}
                  </p>
                </div>
                <span v-else-if="item.latestApplication" class="text-sm text-gray-600 dark:text-gray-300">
                  {{ statusLabel(item.latestApplication.status) }}
                </span>
                <span v-else class="text-sm text-gray-400">无申请</span>
              </td>
              <td class="table-td">{{ displayValue(formatOptionalDateTime(item.profile.certifiedAt)) }}</td>
              <td class="table-td text-right">
                <button class="text-sm font-semibold text-indigo-600 hover:text-indigo-800" type="button" @click="selectTeacher(item.profile.id)">
                  查看
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="teacherPagination.total > 0" class="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/60">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700 dark:text-gray-300">共 {{ teacherPagination.total }} 位教师</div>
          <div class="flex gap-2">
            <button class="page-btn" type="button" :disabled="teacherPagination.page === 1" @click="prevTeacherPage">上一页</button>
            <button class="page-btn" type="button" :disabled="teacherPagination.page * teacherPagination.limit >= teacherPagination.total" @click="nextTeacherPage">下一页</button>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div v-if="loading" class="flex min-h-[280px] items-center justify-center">
          <div class="text-center">
            <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
            <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载认证申请...</p>
          </div>
        </div>

        <div v-else-if="applications.length === 0" class="px-6 py-14 text-center">
          <p class="text-base font-medium text-gray-700 dark:text-gray-200">暂无认证申请</p>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">默认显示待审核，切换筛选可以查看历史记录。</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900/60">
              <tr>
                <th class="table-th">租户</th>
                <th class="table-th">教师</th>
                <th class="table-th">类型</th>
                <th class="table-th">状态</th>
                <th class="table-th">提交时间</th>
                <th class="table-th text-right">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="item in applications"
                :key="item.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-900/40"
                :class="selectedApplication?.id === item.id ? 'bg-indigo-50/70 dark:bg-indigo-900/20' : ''"
              >
                <td class="table-td font-medium text-gray-900 dark:text-gray-100">{{ item.tenantName || item.tenantId }}</td>
                <td class="table-td">
                  <p class="font-medium text-gray-900 dark:text-gray-100">{{ item.displayName || item.tutorUsername || `教师 #${item.tutorId}` }}</p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">用户 ID {{ item.tutorId }}</p>
                </td>
                <td class="table-td">{{ applicationTypeLabel(item.applicationType) }}</td>
                <td class="table-td">
                  <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass(item.status)">
                    {{ statusLabel(item.status) }}
                  </span>
                </td>
                <td class="table-td">{{ formatDateTime(item.submittedAt || item.updatedAt) }}</td>
                <td class="table-td text-right">
                  <button class="text-sm font-semibold text-indigo-600 hover:text-indigo-800" type="button" @click="selectApplication(item.id)">
                    查看
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="applicationPagination.total > 0" class="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/60">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700 dark:text-gray-300">共 {{ applicationPagination.total }} 条</div>
            <div class="flex gap-2">
              <button class="page-btn" type="button" :disabled="applicationPagination.page === 1" @click="prevApplicationPage">上一页</button>
              <button class="page-btn" type="button" :disabled="applicationPagination.page * applicationPagination.limit >= applicationPagination.total" @click="nextApplicationPage">下一页</button>
            </div>
          </div>
        </div>
    </section>

    <div
      v-if="selectedTeacher"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="closeTeacherModal"
    >
      <section class="flex max-h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
        <header class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
          <div>
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">
              {{ selectedTeacher.tenantName || `租户 #${selectedTeacher.profile.tenantId}` }}
            </p>
            <h2 class="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{{ teacherDisplayName(selectedTeacher) }}</h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              用户 ID {{ selectedTeacher.profile.tutorId }} · 申请记录 {{ selectedTeacher.applicationCount }} 条
            </p>
          </div>
          <div class="flex items-center gap-3">
            <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" :class="certificationStatusClass(selectedTeacher.profile.certificationStatus)">
              {{ certificationStatusLabel(selectedTeacher.profile.certificationStatus) }}
            </span>
            <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" :class="teachingStatusClass(selectedTeacher.profile.teachingStatus)">
              {{ teachingStatusLabel(selectedTeacher.profile.teachingStatus) }}
            </span>
            <button
              type="button"
              class="rounded-full px-3 py-1 text-sm font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              @click="closeTeacherModal"
            >
              关闭
            </button>
          </div>
        </header>

        <div class="overflow-y-auto px-6 py-5">
          <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div class="space-y-5">
              <template v-if="!selectedTeacherApplication">
                <section class="detail-section">
                  <h3 class="detail-title">当前线上资料</h3>
                  <div class="mt-4 flex flex-col gap-4 rounded-2xl bg-gray-50 p-4 dark:bg-gray-900/70 sm:flex-row sm:items-center">
                    <button
                      v-if="selectedTeacher.profile.avatarUrl"
                      type="button"
                      class="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-950"
                      @click="openPreview({
                        title: '教师头像',
                        url: selectedTeacher.profile.avatarUrl || '',
                        mimeType: 'image/*',
                        fileName: 'avatar',
                      })"
                    >
                      <img :src="selectedTeacher.profile.avatarUrl" alt="教师头像" class="h-full w-full object-cover" />
                    </button>
                    <div v-else class="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-gray-200 text-2xl font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                      {{ teacherInitial(selectedTeacher) }}
                    </div>
                    <div class="min-w-0">
                      <p class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ teacherDisplayName(selectedTeacher) }}</p>
                      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ displayValue(selectedTeacher.profile.headline) }}</p>
                    </div>
                  </div>
                  <div class="mt-4 grid gap-3 md:grid-cols-2">
                    <div class="detail-row"><span>展示名</span><strong>{{ displayValue(selectedTeacher.profile.displayName) }}</strong></div>
                    <div class="detail-row"><span>账号</span><strong>{{ displayValue(selectedTeacher.tutorUsername) }}</strong></div>
                    <div class="detail-row md:col-span-2"><span>标题</span><strong>{{ displayValue(selectedTeacher.profile.headline) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>自我介绍</span><strong>{{ displayValue(selectedTeacher.profile.description) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>教学经验</span><strong>{{ displayValue(selectedTeacher.profile.teachingBackground) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>教学方式</span><strong>{{ displayValue(selectedTeacher.profile.teachingStyle) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>适合学生</span><strong>{{ displayValue(selectedTeacher.profile.suitableFor) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>给学生的话</span><strong>{{ displayValue(selectedTeacher.profile.studentMessage) }}</strong></div>
                    <div class="detail-row"><span>授课语言</span><strong>{{ selectedTeacher.profile.spokenLanguages?.join("、") || "未填写" }}</strong></div>
                    <div class="detail-row"><span>教学标签</span><strong>{{ selectedTeacher.profile.teachingTags?.join("、") || "未填写" }}</strong></div>
                  </div>
                </section>

                <section class="detail-section">
                  <h3 class="detail-title">当前线上材料</h3>
                  <div v-if="selectedTeacherOnlineApplication?.materials.length" class="mt-4 grid gap-3 md:grid-cols-2">
                    <div v-for="material in selectedTeacherOnlineApplication.materials" :key="material.id" class="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-950">
                      <p class="font-semibold text-gray-900 dark:text-gray-100">{{ materialTypeLabel(material.materialType) }}</p>
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(material.fileSize) }}</p>
                      <video
                        v-if="material.materialType === 'intro_video' && material.fileUrl"
                        :src="material.fileUrl"
                        class="mt-3 aspect-video w-full rounded-lg bg-black"
                        controls
                        preload="metadata"
                      />
                      <button
                        v-else-if="material.fileUrl"
                        class="mt-3 inline-flex text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                        type="button"
                        @click="openPreview({
                          title: materialTypeLabel(material.materialType),
                          url: material.fileUrl,
                          mimeType: material.mimeType,
                          fileName: material.fileName,
                        })"
                      >
                        预览材料
                      </button>
                    </div>
                  </div>
                  <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">暂无线上材料</p>
                </section>

                <section class="detail-section">
                  <h3 class="detail-title">教育背景</h3>
                  <div v-if="selectedTeacher.profile.educations.length" class="mt-4 grid gap-3">
                    <div v-for="item in selectedTeacher.profile.educations" :key="item.id" class="detail-card">
                      <p class="font-semibold text-gray-900 dark:text-gray-100">{{ [item.schoolName, item.degree, item.degreeType, item.specialization].filter(Boolean).join(" · ") }}</p>
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ yearRangeFromValues(item.startYear, item.endYear) }}</p>
                    </div>
                  </div>
                  <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">未填写</p>
                </section>

                <section class="detail-section">
                  <h3 class="detail-title">教学 / 工作经历</h3>
                  <div v-if="selectedTeacher.profile.workExperiences.length" class="mt-4 grid gap-3">
                    <div v-for="item in selectedTeacher.profile.workExperiences" :key="item.id" class="detail-card">
                      <p class="font-semibold text-gray-900 dark:text-gray-100">{{ [item.organization, item.role].filter(Boolean).join(" · ") }}</p>
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ yearRangeFromValues(item.startYear, item.endYear, item.isCurrent) }}</p>
                      <p v-if="item.description" class="mt-2 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">{{ item.description }}</p>
                    </div>
                  </div>
                  <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">未填写</p>
                </section>
              </template>

              <template v-else>
                <section class="detail-section">
                  <div class="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 class="detail-title">申请快照</h3>
                      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        #{{ selectedTeacherApplication.id }} · {{ applicationTypeLabel(selectedTeacherApplication.applicationType) }} · {{ displayValue(formatDateTime(selectedTeacherApplication.submittedAt || selectedTeacherApplication.updatedAt)) }}
                      </p>
                    </div>
                    <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass(selectedTeacherApplication.status)">
                      {{ statusLabel(selectedTeacherApplication.status) }}
                    </span>
                  </div>
                  <div class="mt-4 grid gap-3 md:grid-cols-2">
                    <div class="detail-row"><span>展示名</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "display_name") || selectedTeacherApplication.displayName) }}</strong></div>
                    <div class="detail-row"><span>账号</span><strong>{{ displayValue(selectedTeacherApplication.tutorUsername) }}</strong></div>
                    <div class="detail-row md:col-span-2"><span>标题</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "headline")) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>自我介绍</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "description") || selectedTeacherApplication.description) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>教学经验</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "teaching_background")) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>教学方式</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "teaching_style")) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>适合学生</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "suitable_for")) }}</strong></div>
                    <div class="detail-row detail-row-block md:col-span-2"><span>给学生的话</span><strong>{{ displayValue(snapshotText(selectedTeacherApplication, "student_message")) }}</strong></div>
                    <div class="detail-row"><span>授课语言</span><strong>{{ displayArray(selectedTeacherApplication, "spoken_languages") }}</strong></div>
                    <div class="detail-row"><span>教学标签</span><strong>{{ displayArray(selectedTeacherApplication, "teaching_tags") }}</strong></div>
                  </div>
                </section>

                <section class="detail-section">
                  <h3 class="detail-title">申请材料</h3>
                  <div v-if="selectedTeacherApplication.materials.length" class="mt-4 grid gap-3 md:grid-cols-2">
                    <div v-for="material in selectedTeacherApplication.materials" :key="material.id" class="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-950">
                      <p class="truncate font-semibold text-gray-900 dark:text-gray-100">{{ material.fileName || materialTypeLabel(material.materialType) }}</p>
                      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {{ materialTypeLabel(material.materialType) }} · {{ formatFileSize(material.fileSize) }}
                      </p>
                      <video
                        v-if="material.materialType === 'intro_video' && material.fileUrl"
                        :src="material.fileUrl"
                        class="mt-3 aspect-video w-full rounded-lg bg-black"
                        controls
                        preload="metadata"
                      />
                      <button
                        v-else-if="material.fileUrl"
                        class="mt-3 inline-flex text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                        type="button"
                        @click="openPreview({
                          title: material.fileName || materialTypeLabel(material.materialType),
                          url: material.fileUrl,
                          mimeType: material.mimeType,
                          fileName: material.fileName,
                        })"
                      >
                        预览材料
                      </button>
                    </div>
                  </div>
                  <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">暂无已上传材料</p>
                </section>

                <section v-if="selectedTeacherApplication.reviewNote" class="rounded-xl bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  <p class="font-semibold">审核说明</p>
                  <p class="mt-2 whitespace-pre-line">{{ selectedTeacherApplication.reviewNote }}</p>
                </section>
              </template>

            </div>

            <aside class="space-y-5">
              <section class="detail-section">
                <h3 class="detail-title">申请历史</h3>
                <button
                  type="button"
                  class="mt-4 w-full rounded-xl border px-3 py-3 text-left text-sm font-semibold transition"
                  :class="!selectedTeacherApplication ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300' : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-indigo-200 hover:bg-indigo-50 dark:border-gray-700 dark:bg-gray-900/70 dark:text-gray-200 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/40'"
                  @click="showOnlineProfile"
                >
                  当前线上资料
                </button>
                <div v-if="selectedTeacher.applications.length" class="mt-4 space-y-2">
                  <button
                    v-for="application in selectedTeacher.applications"
                    :key="application.id"
                    type="button"
                    class="w-full rounded-xl border px-3 py-3 text-left transition"
                    :class="selectedTeacherApplication?.id === application.id ? 'border-indigo-200 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/40' : 'border-gray-100 bg-gray-50 hover:border-indigo-200 hover:bg-indigo-50 dark:border-gray-700 dark:bg-gray-900/70 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/40'"
                    @click="selectTeacherApplication(application)"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <p class="truncate font-semibold text-gray-900 dark:text-gray-100">
                          #{{ application.id }} · {{ applicationTypeLabel(application.applicationType) }}
                        </p>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {{ displayValue(formatDateTime(application.submittedAt || application.updatedAt)) }}
                        </p>
                      </div>
                      <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass(application.status)">
                        {{ statusLabel(application.status) }}
                      </span>
                    </div>
                    <p v-if="application.reviewNote" class="mt-2 line-clamp-2 text-xs text-amber-700 dark:text-amber-300">
                      {{ application.reviewNote }}
                    </p>
                  </button>
                </div>
                <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">暂无申请历史</p>
              </section>
            </aside>
          </div>
        </div>

        <footer class="border-t border-gray-200 bg-gray-50 px-6 py-5 dark:border-gray-700 dark:bg-gray-950">
          <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              v-if="canSuspendTeacher(selectedTeacher)"
              class="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              type="button"
              :disabled="submitting"
              @click="openTeacherAction('suspend')"
            >
              暂停开课
            </button>
            <button
              v-if="canResumeTeacher(selectedTeacher)"
              class="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              type="button"
              :disabled="submitting"
              @click="openTeacherAction('resume')"
            >
              恢复开课
            </button>
            <button
              v-if="canRevokeTeacher(selectedTeacher)"
              class="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
              type="button"
              :disabled="submitting"
              @click="openTeacherAction('revoke')"
            >
              撤销认证
            </button>
          </div>
        </footer>
      </section>
    </div>

    <div
      v-if="teacherAction"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      @click.self="closeTeacherAction"
    >
      <section class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900">
        <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ teacherActionTitle }}</h3>
        <p class="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">{{ teacherActionDescription }}</p>
        <label class="mt-5 block">
          <span class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">{{ teacherAction === 'resume' ? '操作说明' : '操作原因' }}</span>
          <textarea
            v-model="teacherActionNote"
            rows="4"
            maxlength="2000"
            class="form-input"
            :class="teacherActionNoteError ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : ''"
            :placeholder="teacherAction === 'resume' ? '可选填写恢复说明' : '请填写具体原因'"
            @input="teacherActionNoteError = ''"
          />
          <p v-if="teacherActionNoteError" class="mt-1 text-xs font-semibold text-rose-600">{{ teacherActionNoteError }}</p>
        </label>
        <div class="mt-6 flex justify-end gap-3">
          <button class="page-btn" type="button" :disabled="submitting" @click="closeTeacherAction">取消</button>
          <button
            class="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            :class="teacherAction === 'revoke' ? 'bg-rose-600 hover:bg-rose-700' : teacherAction === 'suspend' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'"
            type="button"
            :disabled="submitting"
            @click="submitTeacherAction"
          >
            确认
          </button>
        </div>
      </section>
    </div>

    <div
      v-if="selectedApplication"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="closeApplicationModal"
    >
      <section class="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
        <header class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
          <div>
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">
              {{ selectedApplication.tenantName || `租户 #${selectedApplication.tenantId}` }} · {{ applicationTypeLabel(selectedApplication.applicationType) }}
            </p>
            <h2 class="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
              {{ selectedApplication.displayName || selectedApplication.tutorUsername || `教师 #${selectedApplication.tutorId}` }}
            </h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              教师 ID {{ selectedApplication.tutorId }} · 提交时间 {{ displayValue(formatDateTime(selectedApplication.submittedAt || selectedApplication.updatedAt)) }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass(selectedApplication.status)">
              {{ statusLabel(selectedApplication.status) }}
            </span>
            <button
              type="button"
              class="rounded-full px-3 py-1 text-sm font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              @click="closeApplicationModal"
            >
              关闭
            </button>
          </div>
        </header>

        <div class="overflow-y-auto px-6 py-5">
          <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div class="space-y-5">
              <section class="detail-section">
                <h3 class="detail-title">基础信息</h3>
                <div class="mt-4 grid gap-3 md:grid-cols-2">
                  <div class="detail-row"><span>展示名</span><strong>{{ displayValue(snapshotText(selectedApplication, "display_name") || selectedApplication.displayName) }}</strong></div>
                  <div class="detail-row"><span>账号</span><strong>{{ displayValue(selectedApplication.tutorUsername) }}</strong></div>
                  <div class="detail-row md:col-span-2"><span>标题</span><strong>{{ displayValue(snapshotText(selectedApplication, "headline")) }}</strong></div>
                  <div class="detail-row detail-row-block md:col-span-2"><span>自我介绍</span><strong>{{ displayValue(snapshotText(selectedApplication, "description") || selectedApplication.description) }}</strong></div>
                  <div class="detail-row detail-row-block md:col-span-2"><span>教学经验</span><strong>{{ displayValue(snapshotText(selectedApplication, "teaching_background")) }}</strong></div>
                  <div class="detail-row detail-row-block md:col-span-2"><span>教学方式</span><strong>{{ displayValue(snapshotText(selectedApplication, "teaching_style")) }}</strong></div>
                  <div class="detail-row detail-row-block md:col-span-2"><span>适合学生</span><strong>{{ displayValue(snapshotText(selectedApplication, "suitable_for")) }}</strong></div>
                  <div class="detail-row detail-row-block md:col-span-2"><span>给学生的话</span><strong>{{ displayValue(snapshotText(selectedApplication, "student_message")) }}</strong></div>
                  <div class="detail-row"><span>授课语言</span><strong>{{ displayArray(selectedApplication, "spoken_languages") }}</strong></div>
                  <div class="detail-row"><span>教学标签</span><strong>{{ displayArray(selectedApplication, "teaching_tags") }}</strong></div>
                </div>
              </section>

              <section class="detail-section">
                <h3 class="detail-title">教育背景</h3>
                <div v-if="snapshotList(selectedApplication, 'educations').length" class="mt-4 grid gap-3">
                  <div v-for="item in snapshotList(selectedApplication, 'educations')" :key="snapshotItemKey(item)" class="detail-card">
                    <p class="font-semibold text-gray-900 dark:text-gray-100">{{ snapshotItemSummary(item, ['school_name', 'degree', 'degree_type', 'specialization']) }}</p>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ yearRangeText(item) }}</p>
                  </div>
                </div>
                <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">未填写</p>
              </section>

              <section class="detail-section">
                <h3 class="detail-title">教学 / 工作经历</h3>
                <div v-if="snapshotList(selectedApplication, 'work_experiences').length" class="mt-4 grid gap-3">
                  <div v-for="item in snapshotList(selectedApplication, 'work_experiences')" :key="snapshotItemKey(item)" class="detail-card">
                    <p class="font-semibold text-gray-900 dark:text-gray-100">{{ snapshotItemSummary(item, ['organization', 'role']) }}</p>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ yearRangeText(item, Boolean(item.is_current)) }}</p>
                    <p v-if="typeof item.description === 'string' && item.description" class="mt-2 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">{{ item.description }}</p>
                  </div>
                </div>
                <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">未填写</p>
              </section>

              <section class="detail-section">
                <h3 class="detail-title">证书信息</h3>
                <div v-if="snapshotList(selectedApplication, 'certifications').length" class="mt-4 grid gap-3 md:grid-cols-2">
                  <div v-for="item in snapshotList(selectedApplication, 'certifications')" :key="snapshotItemKey(item)" class="detail-card">
                    <p class="font-semibold text-gray-900 dark:text-gray-100">{{ snapshotItemSummary(item, ['certificate_name', 'issued_by', 'subject']) }}</p>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ yearRangeText(item) }}</p>
                    <p v-if="typeof item.description === 'string' && item.description" class="mt-2 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">{{ item.description }}</p>
                  </div>
                </div>
                <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">未填写</p>
              </section>
            </div>

            <aside class="space-y-5">
              <section class="detail-section">
                <h3 class="detail-title">头像</h3>
                <button
                  v-if="selectedApplication.avatarUrl"
                  type="button"
                  class="mt-4 block overflow-hidden rounded-xl border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-950"
                  @click="openPreview({
                    title: '教师头像',
                    url: selectedApplication.avatarUrl || '',
                    mimeType: 'image/*',
                    fileName: 'avatar',
                  })"
                >
                  <img :src="selectedApplication.avatarUrl" alt="教师头像" class="aspect-square w-full object-cover" />
                </button>
                <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">未上传</p>
              </section>

              <section class="detail-section">
                <h3 class="detail-title">提交材料</h3>
                <div v-if="selectedApplication.materials.length" class="mt-4 space-y-3">
                  <div v-for="material in selectedApplication.materials" :key="material.id" class="rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-950">
                    <p class="truncate font-semibold text-gray-900 dark:text-gray-100">{{ material.fileName || materialTypeLabel(material.materialType) }}</p>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {{ materialTypeLabel(material.materialType) }} · {{ formatFileSize(material.fileSize) }}
                    </p>
                    <video
                      v-if="material.materialType === 'intro_video' && material.fileUrl"
                      :src="material.fileUrl"
                      class="mt-3 aspect-video w-full rounded-lg bg-black"
                      controls
                      preload="metadata"
                    />
                    <button
                      v-else-if="material.fileUrl"
                      class="mt-3 inline-flex text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                      type="button"
                      @click="openPreview({
                        title: material.fileName || materialTypeLabel(material.materialType),
                        url: material.fileUrl,
                        mimeType: material.mimeType,
                        fileName: material.fileName,
                      })"
                    >
                      预览材料
                    </button>
                  </div>
                </div>
                <p v-else class="mt-4 text-sm text-gray-500 dark:text-gray-400">暂无已上传材料</p>
              </section>

              <section v-if="selectedApplication.reviewNote" class="rounded-xl bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                <p class="font-semibold">历史审核说明</p>
                <p class="mt-2 whitespace-pre-line">{{ selectedApplication.reviewNote }}</p>
              </section>
            </aside>
          </div>
        </div>

        <footer v-if="selectedApplication.status === 'pending'" class="border-t border-gray-200 bg-gray-50 px-6 py-5 dark:border-gray-700 dark:bg-gray-950">
          <div class="space-y-4">
            <label>
              <span class="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">审核说明</span>
              <textarea
                v-model="reviewNote"
                rows="3"
                maxlength="2000"
                class="form-input"
                :class="reviewNoteError ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : ''"
                placeholder="通过可选；要求修改或拒绝时必须填写具体原因"
                @input="reviewNoteError = ''"
              />
              <p v-if="reviewNoteError" class="mt-1 text-xs font-semibold text-rose-600">{{ reviewNoteError }}</p>
            </label>
            <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button class="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" :disabled="submitting" @click="submitReview('approve')">
                通过
              </button>
              <button class="rounded-xl bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60" :disabled="submitting" @click="submitReview('revision')">
                要求修改
              </button>
              <button class="rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60" :disabled="submitting" @click="submitReview('reject')">
                拒绝
              </button>
            </div>
          </div>
        </footer>
      </section>
    </div>

    <div
      v-if="previewFile"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      @click.self="closePreview"
    >
      <div class="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
        <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ previewFile.title }}</p>
          <button
            type="button"
            class="rounded-full px-3 py-1 text-sm font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            @click="closePreview"
          >
            关闭
          </button>
        </div>
        <div class="flex min-h-[50vh] items-center justify-center bg-gray-100 p-4 dark:bg-gray-950">
          <img
            v-if="previewFile.isImage"
            :src="previewFile.url"
            :alt="previewFile.title"
            class="max-h-[78vh] max-w-full object-contain"
          />
          <iframe
            v-else
            :src="previewFile.url"
            class="h-[78vh] w-full rounded-lg bg-white"
            title="材料预览"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";

import { getTenantsAPI } from "@/api/admin/tenants";
import {
  approveAdminTutorProfileReviewAPI,
  getAdminTutorCertificationTeacherAPI,
  getAdminTutorCertificationTeachersAPI,
  getAdminTutorProfileReviewAPI,
  getAdminTutorProfileReviewsAPI,
  rejectAdminTutorProfileReviewAPI,
  resumeAdminTutorCertificationTeacherAPI,
  requestRevisionAdminTutorProfileReviewAPI,
  revokeAdminTutorCertificationTeacherAPI,
  suspendAdminTutorCertificationTeacherAPI,
} from "@/api/admin/tutorProfileReview";
import type { AdminTenantConfig } from "@/types/admin/tenant";
import type {
  TutorCertificationStatus,
  TutorCertificationTeacher,
  TutorCertificationTeacherDetail,
  TutorProfileReviewApplication,
  TutorProfileReviewApplicationStatus,
  TutorProfileReviewApplicationType,
  TutorProfileReviewMaterialType,
  TutorTeachingStatus,
} from "@/types/admin/tutorProfileReview";
import { formatDateTime } from "@/utils/formatters";
import toaster from "@/utils/toaster";

type ReviewAction = "approve" | "reject" | "revision";
type PageTab = "teachers" | "applications";
type TeacherAction = "suspend" | "resume" | "revoke";

interface PreviewFile {
  title: string;
  url: string;
  mimeType: string | null;
  fileName: string | null;
  isImage: boolean;
}

const tenants = ref<AdminTenantConfig[]>([]);
const activeTab = ref<PageTab>("applications");
const teachers = ref<TutorCertificationTeacher[]>([]);
const selectedTeacher = ref<TutorCertificationTeacherDetail | null>(null);
const selectedTeacherApplication = ref<TutorProfileReviewApplication | null>(null);
const applications = ref<TutorProfileReviewApplication[]>([]);
const selectedApplication = ref<TutorProfileReviewApplication | null>(null);
const loading = ref(false);
const submitting = ref(false);
const reviewNote = ref("");
const reviewNoteError = ref("");
const previewFile = ref<PreviewFile | null>(null);
const teacherAction = ref<TeacherAction | null>(null);
const teacherActionNote = ref("");
const teacherActionNoteError = ref("");

const teacherFilters = reactive<{
  certificationStatus: TutorCertificationStatus | "";
  teachingStatus: TutorTeachingStatus | "";
  tenantId: number;
}>({
  certificationStatus: "",
  teachingStatus: "",
  tenantId: 0,
});

const applicationFilters = reactive<{ status: TutorProfileReviewApplicationStatus | ""; tenantId: number }>({
  status: "pending",
  tenantId: 0,
});
const teacherPagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
});
const applicationPagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
});

const statusOptions: Array<{ label: string; value: TutorProfileReviewApplicationStatus }> = [
  { label: "待审核", value: "pending" },
  { label: "需修改", value: "revision_required" },
  { label: "已通过", value: "approved" },
  { label: "已拒绝", value: "rejected" },
  { label: "已取消", value: "cancelled" },
];

const certificationStatusOptions: Array<{ label: string; value: TutorCertificationStatus }> = [
  { label: "未认证", value: "none" },
  { label: "待审核", value: "pending" },
  { label: "需修改", value: "revision_required" },
  { label: "已拒绝", value: "rejected" },
  { label: "已认证", value: "certified" },
  { label: "已撤销", value: "revoked" },
];

const teachingStatusOptions: Array<{ label: string; value: TutorTeachingStatus }> = [
  { label: "不可开课", value: "disabled" },
  { label: "可开课", value: "enabled" },
  { label: "已暂停", value: "suspended" },
];

const statusLabel = (status: TutorProfileReviewApplicationStatus): string => {
  const map: Record<TutorProfileReviewApplicationStatus, string> = {
    draft: "草稿",
    pending: "待审核",
    revision_required: "需修改",
    approved: "已通过",
    rejected: "已拒绝",
    cancelled: "已取消",
  };
  return map[status];
};

const certificationStatusLabel = (status: TutorCertificationStatus): string => {
  const map: Record<TutorCertificationStatus, string> = {
    none: "普通教师",
    pending: "认证审核中",
    revision_required: "需修改",
    rejected: "已拒绝",
    certified: "认证教师",
    revoked: "已撤销",
  };
  return map[status];
};

const certificationStatusClass = (status: TutorCertificationStatus): string => {
  if (status === "certified") return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  if (status === "pending") return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
  if (status === "revision_required") return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  if (status === "rejected" || status === "revoked") return "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
};

const teachingStatusLabel = (status: TutorTeachingStatus): string => {
  const map: Record<TutorTeachingStatus, string> = {
    disabled: "不可开课",
    enabled: "可开课",
    suspended: "已暂停",
  };
  return map[status];
};

const teachingStatusClass = (status: TutorTeachingStatus): string => {
  if (status === "enabled") return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  if (status === "suspended") return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
};

const statusClass = (status: TutorProfileReviewApplicationStatus): string => {
  if (status === "pending") return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
  if (status === "revision_required") return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  if (status === "approved") return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  if (status === "rejected") return "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
};

const applicationTypeLabel = (type: TutorProfileReviewApplicationType): string => {
  return type === "profile_update" ? "资料更新" : "首次认证";
};

const materialTypeLabel = (type: TutorProfileReviewMaterialType): string => {
  const map: Record<TutorProfileReviewMaterialType, string> = {
    intro_video: "介绍视频",
    certificate: "资质证书",
    identity: "身份证明",
    other: "其他",
  };
  return map[type];
};

const snapshotText = (application: TutorProfileReviewApplication, key: string): string => {
  const value = application.profileSnapshotJson?.[key];
  return typeof value === "string" ? value.trim() : "";
};

const displayValue = (value: string | number | null | undefined): string => {
  if (typeof value === "number") return String(value);
  const trimmed = (value || "").trim();
  return trimmed || "未填写";
};

const formatOptionalDateTime = (value: string | null | undefined): string => {
  return value ? formatDateTime(value) : "";
};

const teacherDisplayName = (item: TutorCertificationTeacher | TutorCertificationTeacherDetail): string => {
  return item.profile.displayName || item.tutorUsername || `教师 #${item.profile.tutorId}`;
};

const teacherInitial = (item: TutorCertificationTeacher | TutorCertificationTeacherDetail): string => {
  return teacherDisplayName(item).trim().slice(0, 1).toUpperCase() || "T";
};

const yearRangeFromValues = (startYear: number | null, endYear: number | null, isCurrent = false): string => {
  if (!startYear && !endYear && !isCurrent) return "";
  const endText = isCurrent ? "至今" : endYear ? String(endYear) : "";
  if (startYear && endText) return `${startYear} - ${endText}`;
  return startYear ? String(startYear) : endText;
};

const canSuspendTeacher = (item: TutorCertificationTeacher | TutorCertificationTeacherDetail): boolean => {
  return item.profile.certificationStatus === "certified" && item.profile.teachingStatus === "enabled";
};

const canResumeTeacher = (item: TutorCertificationTeacher | TutorCertificationTeacherDetail): boolean => {
  return item.profile.certificationStatus === "certified" && item.profile.teachingStatus === "suspended";
};

const canRevokeTeacher = (item: TutorCertificationTeacher | TutorCertificationTeacherDetail): boolean => {
  return item.profile.certificationStatus === "certified";
};

const teacherActionTitle = computed(() => {
  if (teacherAction.value === "suspend") return "暂停教师开课";
  if (teacherAction.value === "resume") return "恢复教师开课";
  if (teacherAction.value === "revoke") return "撤销教师认证";
  return "";
});

const teacherActionDescription = computed(() => {
  if (teacherAction.value === "suspend") {
    return "暂停后，该老师会保留认证身份，但不能继续展示可预约入口或开放新预约。";
  }
  if (teacherAction.value === "resume") {
    return "恢复后，该老师重新具备开课资格，学生端会按课程和可约时间展示预约入口。";
  }
  if (teacherAction.value === "revoke") {
    return "撤销后，该老师将失去认证教师资格，需要重新提交 Level 2 认证后才能开课。历史订单和审核记录不会删除。";
  }
  return "";
});

const selectedTeacherOnlineApplication = computed(() => {
  if (!selectedTeacher.value?.profile.approvedApplicationId) return null;
  return selectedTeacher.value.applications.find(
    (application) => application.id === selectedTeacher.value?.profile.approvedApplicationId,
  ) || null;
});

const displayArray = (application: TutorProfileReviewApplication, key: string): string => {
  const value = application.profileSnapshotJson?.[key];
  if (!Array.isArray(value)) return "未填写";
  return value
    .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    .join("、") || "未填写";
};

const snapshotList = (application: TutorProfileReviewApplication, key: string): Record<string, unknown>[] => {
  const value = application.profileSnapshotJson?.[key];
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => !!item && typeof item === "object") : [];
};

const snapshotItemSummary = (item: Record<string, unknown>, keys: string[]): string => {
  return keys
    .map((key) => item[key])
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(" · ") || "-";
};

const snapshotItemKey = (item: Record<string, unknown>): string => {
  return JSON.stringify(item);
};

const yearRangeText = (item: Record<string, unknown>, isCurrent = false): string => {
  const start = typeof item.start_year === "number" ? item.start_year : null;
  const end = typeof item.end_year === "number" ? item.end_year : null;
  if (!start && !end && !isCurrent) return "";
  const endText = isCurrent ? "至今" : end ? String(end) : "";
  if (start && endText) return `${start} - ${endText}`;
  return start ? String(start) : endText;
};

const formatFileSize = (size: number | null): string => {
  if (!size) return "--";
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const fileExtension = (fileName: string | null): string => {
  const value = fileName || "";
  const extension = value.includes(".") ? value.split(".").pop() : "";
  return (extension || "").toLowerCase();
};

const isImageFile = (mimeType: string | null, fileName: string | null): boolean => {
  if ((mimeType || "").startsWith("image/")) return true;
  return ["jpg", "jpeg", "png", "webp", "gif"].includes(fileExtension(fileName));
};

const openPreview = (file: Omit<PreviewFile, "isImage">) => {
  previewFile.value = {
    ...file,
    isImage: isImageFile(file.mimeType, file.fileName),
  };
};

const closePreview = () => {
  previewFile.value = null;
};

const loadTenants = async () => {
  const res = await getTenantsAPI({ page: 1, limit: 200 });
  if (res.status === 200 && res.data) {
    tenants.value = res.data.tenants;
  }
};

const loadTeachers = async () => {
  loading.value = true;
  try {
    const res = await getAdminTutorCertificationTeachersAPI({
      page: teacherPagination.page,
      limit: teacherPagination.limit,
      tenantId: teacherFilters.tenantId || null,
      certificationStatus: teacherFilters.certificationStatus,
      teachingStatus: teacherFilters.teachingStatus,
    });
    if (res.status === 200 && res.data) {
      teachers.value = res.data.teachers;
      teacherPagination.total = res.data.total;
      teacherPagination.page = res.data.page;
      teacherPagination.limit = res.data.limit;
    } else {
      toaster.error(res.message || "加载教师认证状态失败");
    }
  } catch (error) {
    toaster.error("加载教师认证状态失败");
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const loadApplications = async () => {
  loading.value = true;
  try {
    const res = await getAdminTutorProfileReviewsAPI({
      page: applicationPagination.page,
      limit: applicationPagination.limit,
      status: applicationFilters.status,
      tenantId: applicationFilters.tenantId || null,
    });
    if (res.status === 200 && res.data) {
      applications.value = res.data.applications;
      applicationPagination.total = res.data.total;
      applicationPagination.page = res.data.page;
      applicationPagination.limit = res.data.limit;
      if (selectedApplication.value) {
        selectedApplication.value = applications.value.find((item) => item.id === selectedApplication.value?.id) || null;
      }
    } else {
      toaster.error(res.message || "加载认证申请失败");
    }
  } catch (error) {
    toaster.error("加载认证申请失败");
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const reloadActiveTab = () => {
  if (activeTab.value === "teachers") {
    loadTeachers();
    return;
  }
  loadApplications();
};

const switchTab = (tab: PageTab) => {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  selectedTeacher.value = null;
  selectedApplication.value = null;
  reloadActiveTab();
};

const selectTeacher = async (profileId: number | null) => {
  if (!profileId) return;
  const res = await getAdminTutorCertificationTeacherAPI(profileId);
  if (res.status === 200 && res.data) {
    selectedTeacher.value = res.data;
    selectedTeacherApplication.value = null;
    teacherAction.value = null;
    teacherActionNote.value = "";
    teacherActionNoteError.value = "";
  } else {
    toaster.error(res.message || "加载教师认证详情失败");
  }
};

const selectApplication = async (applicationId: number) => {
  const res = await getAdminTutorProfileReviewAPI(applicationId);
  if (res.status === 200 && res.data) {
    selectedApplication.value = res.data;
    reviewNote.value = "";
    reviewNoteError.value = "";
  } else {
    toaster.error(res.message || "加载申请详情失败");
  }
};

const selectTeacherApplication = (application: TutorProfileReviewApplication) => {
  selectedTeacherApplication.value = application;
};

const showOnlineProfile = () => {
  selectedTeacherApplication.value = null;
};

const closeTeacherModal = () => {
  selectedTeacher.value = null;
  selectedTeacherApplication.value = null;
  closeTeacherAction();
};

const closeApplicationModal = () => {
  selectedApplication.value = null;
  reviewNote.value = "";
  reviewNoteError.value = "";
};

const handleTeacherFilterChange = () => {
  teacherPagination.page = 1;
  selectedTeacher.value = null;
  loadTeachers();
};

const handleApplicationFilterChange = () => {
  applicationPagination.page = 1;
  selectedApplication.value = null;
  loadApplications();
};

const prevTeacherPage = () => {
  if (teacherPagination.page <= 1) return;
  teacherPagination.page -= 1;
  loadTeachers();
};

const nextTeacherPage = () => {
  if (teacherPagination.page * teacherPagination.limit >= teacherPagination.total) return;
  teacherPagination.page += 1;
  loadTeachers();
};

const prevApplicationPage = () => {
  if (applicationPagination.page <= 1) return;
  applicationPagination.page -= 1;
  loadApplications();
};

const nextApplicationPage = () => {
  if (applicationPagination.page * applicationPagination.limit >= applicationPagination.total) return;
  applicationPagination.page += 1;
  loadApplications();
};

const replaceApplication = (nextApplication: TutorProfileReviewApplication) => {
  applications.value = applications.value.map((item) => (item.id === nextApplication.id ? nextApplication : item));
  selectedApplication.value = nextApplication;
};

const replaceTeacher = (nextTeacher: TutorCertificationTeacherDetail) => {
  teachers.value = teachers.value.map((item) => (
    item.profile.id === nextTeacher.profile.id ? nextTeacher : item
  ));
  selectedTeacher.value = nextTeacher;
  if (selectedTeacherApplication.value) {
    selectedTeacherApplication.value = nextTeacher.applications.find((item) => item.id === selectedTeacherApplication.value?.id) || null;
  }
};

const openTeacherAction = (action: TeacherAction) => {
  teacherAction.value = action;
  teacherActionNote.value = "";
  teacherActionNoteError.value = "";
};

const closeTeacherAction = () => {
  teacherAction.value = null;
  teacherActionNote.value = "";
  teacherActionNoteError.value = "";
};

const submitTeacherAction = async () => {
  if (!selectedTeacher.value || !selectedTeacher.value.profile.id || !teacherAction.value || submitting.value) return;
  teacherActionNoteError.value = "";
  if (teacherAction.value !== "resume" && !teacherActionNote.value.trim()) {
    teacherActionNoteError.value = teacherAction.value === "suspend" ? "请填写暂停开课原因" : "请填写撤销认证原因";
    return;
  }
  submitting.value = true;
  try {
    const profileId = selectedTeacher.value.profile.id;
    const payload = { reviewNote: teacherActionNote.value.trim() || null };
    const res = teacherAction.value === "suspend"
      ? await suspendAdminTutorCertificationTeacherAPI(profileId, payload)
      : teacherAction.value === "resume"
        ? await resumeAdminTutorCertificationTeacherAPI(profileId, payload)
        : await revokeAdminTutorCertificationTeacherAPI(profileId, payload);
    if (res.status === 200 && res.data) {
      replaceTeacher(res.data);
      closeTeacherAction();
      toaster.success(res.message || "操作成功");
      await loadTeachers();
    } else {
      toaster.error(res.message || "操作失败");
    }
  } catch (error) {
    toaster.error("操作失败");
    console.error(error);
  } finally {
    submitting.value = false;
  }
};

const submitReview = async (action: ReviewAction) => {
  if (!selectedApplication.value || submitting.value) return;
  reviewNoteError.value = "";
  if ((action === "revision" || action === "reject") && !reviewNote.value.trim()) {
    reviewNoteError.value = action === "revision" ? "要求修改时必须填写审核说明" : "拒绝时必须填写审核说明";
    return;
  }
  submitting.value = true;
  try {
    const payload = { reviewNote: reviewNote.value.trim() || null };
    const applicationId = selectedApplication.value.id;
    const res = action === "approve"
      ? await approveAdminTutorProfileReviewAPI(applicationId, payload)
      : action === "reject"
        ? await rejectAdminTutorProfileReviewAPI(applicationId, payload)
        : await requestRevisionAdminTutorProfileReviewAPI(applicationId, payload);

    if (res.status === 200 && res.data) {
      replaceApplication(res.data);
      reviewNote.value = "";
      toaster.success(action === "approve" ? "已通过认证" : "审核结果已提交");
      await loadTeachers();
    } else {
      toaster.error(res.message || "处理失败");
    }
  } catch (error) {
    toaster.error("处理失败");
    console.error(error);
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  loadTenants();
  loadApplications();
});
</script>

<style scoped>
.form-input {
  @apply w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:focus:ring-indigo-900/40;
}

.table-th {
  @apply px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400;
}

.table-td {
  @apply px-4 py-4 text-sm text-gray-600 dark:text-gray-300;
}

.page-btn {
  @apply rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700;
}

.tab-btn {
  @apply flex-1 rounded-xl px-4 py-3 text-sm font-semibold text-gray-500 transition hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700;
}

.tab-btn-active {
  @apply bg-indigo-600 text-white shadow-sm hover:bg-indigo-600 hover:text-white dark:bg-indigo-500 dark:hover:bg-indigo-500;
}

.detail-section {
  @apply rounded-xl border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800;
}

.detail-title {
  @apply text-base font-bold text-gray-900 dark:text-gray-100;
}

.detail-row {
  @apply rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-900/70;
}

.detail-row-block {
  @apply min-h-[5rem];
}

.detail-row span {
  @apply block text-xs font-semibold text-gray-400 dark:text-gray-500;
}

.detail-row strong {
  @apply mt-1 block whitespace-pre-line break-words font-semibold text-gray-900 dark:text-gray-100;
}

.detail-card {
  @apply rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/70;
}
</style>
